// @ts-nocheck: converted from JS

import { FilterTypes as filterType } from '@/constants/FilterTypes';
import { _isNotEmpty, _notNil } from '@/littledash';
import { api as apiRoute } from '@/support/route';
import type { TableFilter } from './APITable.model';

// ================================================================
// COLUMN VISIBILITY HELPERS
// ================================================================

/**
 * Columns whose visibility should always be controlled by their isVisible prop,
 * not by saved user settings. This ensures permission-dependent columns (like actions)
 * respond correctly when permissions change.
 */
const SYSTEM_COLUMNS = ['selection', 'actions'];

/**
 * Merges saved column visibility with defaults, excluding system columns from saved settings.
 * System columns are always controlled by their column definition's isVisible prop.
 */
export const mergeColumnVisibility = (
  savedVisibility: Record<string, boolean> | undefined,
  defaultVisibility: Record<string, boolean>
): Record<string, boolean> => {
  if (!savedVisibility) {
    return defaultVisibility;
  }

  const filteredSaved = Object.entries(savedVisibility).reduce<Record<string, boolean>>((acc, [key, value]) => {
    if (!SYSTEM_COLUMNS.includes(key)) {
      acc[key] = value;
    }
    return acc;
  }, {});

  return { ...defaultVisibility, ...filteredSaved };
};

/**
 * Filters out system columns from visibility state before saving.
 */
export const filterSystemColumnsFromVisibility = (visibility: Record<string, boolean>): Record<string, boolean> => {
  return Object.entries(visibility).reduce<Record<string, boolean>>((acc, [key, value]) => {
    if (!SYSTEM_COLUMNS.includes(key)) {
      acc[key] = value;
    }
    return acc;
  }, {});
};

export const resolveSavedSort = (
  savedSort: { id: string; desc: boolean } | null | undefined,
  columns: ReadonlyArray<{ id: string }>,
  defaultSortBy: { id: string; desc: boolean }
): { id: string; desc: boolean } => {
  if (_notNil(savedSort) && columns.some((c) => c.id === savedSort.id)) {
    return { id: savedSort.id, desc: savedSort.desc };
  }
  return { id: defaultSortBy.id, desc: defaultSortBy.desc };
};

/**
 * Drops saved filters whose category is no longer in the table's filterOptions.
 * Same failure mode as resolveSavedSort: a saved view can reference a preset
 * measurement (e.g. `left_flank`) that has since been removed from the preset,
 * which would otherwise be sent to the server as an invalid filter and fail.
 *
 * When filterOptions is empty or not provided, validation is deferred (the
 * parent hasn't declared its categories yet) and the filters are returned
 * unchanged — a follow-up pass, run once filterOptions has populated, will
 * scrub any that turn out to be invalid.
 */
export const resolveSavedFilters = (savedFilters, filterOptions) => {
  if (!_notNil(savedFilters)) {
    return [];
  }
  if (!_isNotEmpty(filterOptions)) {
    return savedFilters;
  }
  const validCategoryValues = new Set(filterOptions.map((option) => String(option?.value)));
  return savedFilters.filter(
    (filter) => _notNil(filter?.category) && validCategoryValues.has(String(filter.category.value))
  );
};

// ================================================================
// FILTER HELPERS
// ================================================================

const getFilterOperation = (name) => Object.keys(filterType).find((key) => filterType[key] === name);

const filterDetails = (catagory, option, value) => `${catagory}|${option}|${value}`;

export const generateFilterParams = (
  filters,
  params,
  customFilter = null,
  customFilterProcessor?: (filters: any[], params: URLSearchParams) => void
) => {
  params.delete('filters[]');

  filters.forEach((filter) => {
    const filterOption = getFilterOperation(filter.operation.name);
    const isNotMetadata = !String(filter.category.value).includes('metadata.');

    if (isNotMetadata) {
      if (filterOption === 'between' || filterOption === 'between_datetime' || filterOption === 'between_interval') {
        if (filter.option.from && filter.option.to) {
          const optionValue = `${filter.option.from},${filter.option.to}`;
          params.append('filters[]', filterDetails(filter.category.value, filterOption, optionValue));
        }
      } else if (filter.option.value !== '') {
        params.append('filters[]', filterDetails(filter.category.value, filterOption, filter.option.value));
      } else if (filter.operation.name === filterType.IS_NULL || filter.operation.name === filterType.EMPTY) {
        params.append('filters[]', filterDetails(filter.category.value, 'eq', 'null'));
      } else if (filter.operation.name === filterType.IS_NOT_NULL) {
        params.append('filters[]', filterDetails(filter.category.value, 'ne', 'null'));
      }
    }
  });

  // Call custom filter processor if provided
  if (customFilterProcessor && typeof customFilterProcessor === 'function') {
    customFilterProcessor(filters, params);
  }

  if (_notNil(customFilter)) {
    const { type, op, filter, value } = customFilter;
    params.set('filterType', type);
    params.append('filters[]', `${filter}|${op}|${value}`);
  }
};

export const getParams = ({
  searchQuery,
  searchQueryBy,
  filters,
  filterMatch,
  sortBy,
  pageSize = 10,
  pageIndex = 1,
  columns,
  includeParam,
  excludeParam,
  resetPage = false,
  customFilter,
  customFilterProcessor,
  queryParams,
  urlSearchParams = new URLSearchParams(),
}: {
  searchQuery?: string;
  searchQueryBy?: string;
  filters?: any[];
  filterMatch?: boolean;
  sortBy?: any[];
  pageSize?: number;
  pageIndex?: number;
  columns?: string[];
  includeParam?: string;
  excludeParam?: any[];
  resetPage?: boolean;
  customFilter?: any;
  customFilterProcessor?: (filters: any[], params: URLSearchParams) => void;
  queryParams?: Record<string, any>;
  urlSearchParams?: URLSearchParams;
}) => {
  if (searchQuery) {
    if (searchQueryBy) {
      urlSearchParams.set('query_by[]', searchQueryBy);
    }
    urlSearchParams.set('query', searchQuery);
  } else {
    urlSearchParams.delete('query');
    urlSearchParams.delete('query_by[]');
  }

  if (_isNotEmpty(filters) || _notNil(customFilter)) {
    urlSearchParams.set('filterType', filterMatch ? 'and' : 'or');
    generateFilterParams(filters, urlSearchParams, customFilter, customFilterProcessor);
  } else {
    urlSearchParams.delete('filters[]');
    urlSearchParams.delete('filterType');
  }
  if (_isNotEmpty(sortBy)) {
    const { id: columnId, desc } = sortBy[0]; // we only filter by 1 sort/order param from our apis
    urlSearchParams.set('order', desc ? 'desc' : 'asc');
    urlSearchParams.set('sort', columnId);
  }
  if (columns) {
    urlSearchParams.set('columns[]', columns);
  }

  if (includeParam) {
    urlSearchParams.set('include', includeParam);
  }

  if (_isNotEmpty(excludeParam)) {
    excludeParam.forEach((exclude) => {
      urlSearchParams.set('excluded_ids[]', exclude);
    });
  }

  if (!resetPage) {
    urlSearchParams.set('page', pageIndex + 1);
  }

  if (_notNil(queryParams)) {
    (Object.keys(queryParams) ?? []).forEach((key) => {
      urlSearchParams.set(key, queryParams[key]);
    });
  }

  urlSearchParams.set('perPage', pageSize);

  return `?${urlSearchParams.toString()}`;
};

export const getExportUrl = (route, queryString) => {
  const url = route.params ? apiRoute(route.url, route.params) : apiRoute(route.url);

  if (route.noFilters) {
    return url;
  }
  return `${url}${queryString}`;
};

export const downloadFile = ({ data, headers }) => {
  const fileName = headers['file-name'];
  const url = window.URL.createObjectURL(new Blob([data]));
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', fileName);
  document.body.appendChild(link);
  link.click();
};

/**
 *
 * @param dispatch {Dispatch<any>}
 * @param settings {Record<string,any>}
 * @param tableName {string}
 * @param attribute {{ key:string, value?: any }}
 */
export const saveTableSettings = (settings) => {
  saveToLocalStorage(settings);
};

export const saveToLocalStorage = (settings) => {
  try {
    const serializedSettings = JSON.stringify(settings);
    localStorage.setItem('settings', serializedSettings);
  } catch {
    // ignore write errors
  }
};

export const loadSettingsFromLocalStorage = () => {
  try {
    const serializedState = localStorage.getItem('settings');
    if (serializedState === null) {
      return defaultSettings;
    }
    return JSON.parse(serializedState);
  } catch (err) {
    return defaultSettings;
  }
};

export const addFilterToFilters = (
  existingFilters: Array<TableFilter> = [],
  newFilter: TableFilter
): Array<TableFilter> => {
  const filters = existingFilters || [];

  // Check if a filter with the same category already exists
  const existingFilterIndex = filters.findIndex(
    (existingFilter) => existingFilter.category?.value === newFilter.category?.value
  );

  let newFilters;
  if (existingFilterIndex >= 0) {
    // Replace existing filter for the same category
    newFilters = [...filters];
    newFilters[existingFilterIndex] = newFilter;
  } else {
    // Add new filter
    newFilters = [...filters, newFilter];
  }

  return newFilters;
};

/**
 * Determines whether initial selection should be performed on table load
 * @param selectAllOnLoad - Feature flag to enable select-all-on-load
 * @param hasPerformedInitialSelection - Guard to ensure selection only happens once
 * @param loading - Loading state of the table
 * @param tableData - Current table data
 * @returns true if initial selection should be performed, false otherwise
 */
export const shouldPerformInitialSelection = (
  selectAllOnLoad: boolean,
  hasPerformedInitialSelection: boolean,
  loading: boolean,
  tableData: Array<unknown>
): boolean => {
  return selectAllOnLoad && !hasPerformedInitialSelection && !loading && _isNotEmpty(tableData);
};

/**
 * Determines whether to notify parent component of initial selection
 * Called after toggleAllRowsSelected completes to ensure fresh selection state
 * @param needsNotification - Flag indicating notification is pending
 * @param selectedRowIds - Object mapping selected row IDs (from react-table state)
 * @returns true if parent should be notified with selection data, false otherwise
 */
export const shouldNotifyInitialSelection = (
  needsNotification: boolean,
  selectedRowIds: Record<string, boolean> | undefined
): boolean => {
  if (!needsNotification) {
    return false;
  }
  // Verify that selection state has actually been updated
  // selectedRowIds should be a non-empty object after toggleAllRowsSelected(true)
  return _isNotEmpty(selectedRowIds);
};
